/**
 * A centre's subscription: the seats it has bought.
 *
 * The shape a therapist's has, plus the two things only a centre has —
 * capacity, and the window that holds its members' Plus open after the money
 * stops.
 */

import {
    BillingPeriod,
    ModuleOption,
    PlanTier,
    PlusSubscriptionDetail,
    Quote,
    modulePriceFor,
    toQuote,
    toSubscriptionDetail,
} from "./types";

export interface CenterSubscriptionState {
    /** This centre pays for no seat at all; there is nothing to buy. */
    earlyAdopter: boolean;
    available: boolean;
    /** Capacity bought — what the centre is billed for, filled or not. */
    seats: number;
    /**
     * What it drops to at the renewal, when capacity has been released. Null
     * the rest of the time: the seats were paid for up to that date and are
     * not taken away before it.
     */
    seatsAtRenewal: number | null;
    /**
     * How much of it is spoken for: members who are not early adopters, plus
     * invitations still waiting. An invitation reserves a seat because we
     * cannot know yet whether whoever accepts it will be free for ever.
     */
    seatsUsed: number;
    seatsAvailable: number;
    /**
     * When the members' Plus stops being held open. Null while the centre
     * pays normally — and also once the window has already run out, which the
     * server decides so the screen never has to.
     */
    graceEndsAt: Date | null;
    quotes: Quote[];
    /** Both seat sizes on both periods: a centre buys every seat as Plus or as Max. */
    plusQuotes: Quote[];
    maxQuotes: Quote[];
    /** The paid modules the centre can buy, each priced per seat. */
    modules: ModuleOption[];
    subscription: PlusSubscriptionDetail | null;
}

export interface CenterSubscriptionStateDTO {
    early_adopter?: boolean;
    available: boolean;
    seats: number;
    seats_at_renewal?: number | null;
    seats_used: number;
    seats_available: number;
    grace_ends_at: string | null;
    quotes?: Parameters<typeof toQuote>[0][];
    plus_quotes?: Parameters<typeof toQuote>[0][];
    max_quotes?: Parameters<typeof toQuote>[0][];
    modules?: {
        slug: string;
        name: string;
        tool_id?: string | null;
        monthly_price_cents: number;
        annual_price_cents?: number;
        adopted?: boolean;
    }[];
    subscription: Parameters<typeof toSubscriptionDetail>[0] | null;
}

const toDate = (raw: string | null): Date | null => {
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toCenterSubscriptionState = (
    dto: CenterSubscriptionStateDTO
): CenterSubscriptionState => ({
    earlyAdopter: Boolean(dto.early_adopter),
    available: Boolean(dto.available),
    seats: dto.seats ?? 0,
    seatsAtRenewal: dto.seats_at_renewal ?? null,
    seatsUsed: dto.seats_used ?? 0,
    seatsAvailable: dto.seats_available ?? 0,
    graceEndsAt: toDate(dto.grace_ends_at),
    quotes: (dto.quotes ?? []).map(toQuote),
    plusQuotes: (dto.plus_quotes ?? dto.quotes ?? []).map(toQuote),
    maxQuotes: (dto.max_quotes ?? []).map(toQuote),
    modules: (dto.modules ?? []).map((module) => ({
        slug: module.slug,
        name: module.name,
        toolId: module.tool_id ?? null,
        monthlyPriceCents: module.monthly_price_cents,
        annualPriceCents: module.annual_price_cents ?? 0,
        adopted: Boolean(module.adopted),
    })),
    subscription: dto.subscription ? toSubscriptionDetail(dto.subscription) : null,
});

/** The modules a centre could still tick: the ones it does not hold yet. */
/** The seat quote for a period at a size; without a size, the one that renews. */
export const centerQuoteFor = (
    state: CenterSubscriptionState,
    period: BillingPeriod,
    tier?: PlanTier
): Quote | null => {
    const quotes =
        tier === "max" ? state.maxQuotes : tier === "plus" ? state.plusQuotes : state.quotes;
    return quotes.find((quote) => quote.period === period) ?? null;
};

export const addableCenterModules = (
    state: CenterSubscriptionState
): ModuleOption[] => state.modules.filter((module) => !module.adopted);

/** The fewest seats this centre may drop to without stranding somebody. */
export const lowestSeatCount = (state: CenterSubscriptionState): number =>
    state.seatsUsed;

export interface SeatPreviewLine {
    key: string;
    label: string;
    quantity: number;
    totalCents: number;
}

export interface SeatPreview {
    lines: SeatPreviewLine[];
    totalCents: number;
}

/**
 * What a quote comes to at a number of seats the server has not been told yet.
 *
 * A centre chooses how many seats it is buying before there is anything to
 * quote against, so the screen has to show the cost of a number the server
 * does not hold. Every line of a centre's quote is priced **per seat** — the
 * seat itself and each module the centre switched on — so the chosen number
 * is the quantity of all of them.
 *
 * No pricing rule is reproduced here. The unit price arrives from the server
 * already priced for the period, discount and all; this only multiplies it by
 * a quantity, which is the same multiplication the server does. Nothing is
 * claimed about the saving, because that is not a figure this can work out
 * from one quote.
 *
 * `adding` are modules ticked for this purchase and not the centre's yet, for
 * the same reason: the server's quote is of what the centre holds, and the
 * total on screen has to be of what it is about to buy.
 */
export const seatPreview = (
    quote: Quote,
    seats: number,
    adding: ModuleOption[] = [],
    period: BillingPeriod = "monthly"
): SeatPreview => {
    const lines = quote.lines.map((line) => ({
        key: line.key,
        label: line.label,
        quantity: seats,
        totalCents: line.unitCents * seats,
    }));

    // Modules ticked for this same purchase. The server's quote cannot carry
    // them — it does not know what has been ticked — so they are added here,
    // at the unit price it already priced for the period, and multiplied by
    // the same seat count as everything else. No pricing rule is reproduced:
    // the annual figure arrives from the server with its discount in it.
    const held = new Set(lines.map((line) => line.key));
    for (const module of adding) {
        if (held.has(module.slug)) continue;
        held.add(module.slug);
        lines.push({
            key: module.slug,
            label: module.name,
            quantity: seats,
            totalCents: modulePriceFor(module, period) * seats,
        });
    }

    return {
        lines,
        totalCents: lines.reduce((total, line) => total + line.totalCents, 0),
    };
};
